import React, { useState, useEffect } from 'react';

interface HeroSectionProps {
  openPopup?: (config?: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  }) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ openPopup }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const words = ['Career', 'Journey', 'Future', 'Success', 'Potential'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
        setIsAnimating(false);
      }, 300);
    }, 2500); // Change word every 2.5 seconds
    
    return () => clearInterval(interval);
  }, [words.length]);


    return (
    <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white h-screen flex items-center justify-center overflow-hidden z-10">
      {/* Background video element */}
      <div className="absolute inset-0">
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline 
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              backgroundImage: "url('https://cdn.prod.website-files.com/64f6f2c0e3f4c5a91c1e823a%2F67b7b1c276fa500cc47a35a0_Hero_1_circle-poster-00001.jpg')"
            }}
          >
            <source 
              src="https://cdn.prod.website-files.com/64f6f2c0e3f4c5a91c1e823a%2F67b7b1c276fa500cc47a35a0_Hero_1_circle-transcode.mp4" 
              type="video/mp4" 
            />
            <source 
              src="https://cdn.prod.website-files.com/64f6f2c0e3f4c5a91c1e823a%2F67b7b1c276fa500cc47a35a0_Hero_1_circle-transcode.webm" 
              type="video/webm" 
            />
          </video>
        </div>
        
        {/* Overlay to maintain readability */}
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 -mt-32 sm:mt-8">
        <div className="text-center">
          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 leading-tight tracking-tight">
            <div className="mb-1 sm:inline">
              <span className="inline-block animate-pulse">10x</span>{" "}
              <span className="inline-block animate-bounce delay-150">Your</span>
            </div>{" "}
            <span className={`relative inline-block transition-all duration-300 ease-in-out transform ${
              isAnimating ? 'scale-110 rotate-2' : 'scale-100 rotate-0'
            }`}>
              <span className={`text-blue-400 inline-block transition-all duration-300 ${
                isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
              }`}>
                {words[currentWordIndex]}
              </span>
              {/* Glowing effect */}
              <span className="absolute inset-0 text-blue-400 blur-sm opacity-50 animate-pulse">
                {words[currentWordIndex]}
              </span>
              {/* Gradient underline animation */}
              <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 transition-all duration-500 ${
                isAnimating ? 'w-full' : 'w-0'
              }`}></div>
            </span>
          </h1>
          
          {/* Subtitle */}
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
            <p className="text-base sm:text-2xl lg:text-3xl text-white font-light leading-relaxed">
              Choose tech courses that fit your goals and{" "}
              <br className="hidden sm:block" />
              shape a successful digital future.
            </p>
          </div>
          
          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4">
            <a 
              href="#featured-programs"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
              style={{ minWidth: 'max-content' }}
            >
              Explore Courses
            </a>
            <button 
              onClick={() => openPopup?.({
                title: "Book FREE Strategy Call",
                subtitle: "Connect with our career transformation experts to discuss your goals and create a personalized roadmap",
                formType: "strategy_call"
              })}
              className="border border-white/50 text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
              style={{ minWidth: 'max-content' }}
            >
              Book FREE Strategy Call
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
